import React, { useMemo } from "react";
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel } from "@fluentui/react-components";
import TruncatedText from "../truncatedText/TruncatedText";
import AssignmentRow from "./AssignmentRow";
import styles from "./resourceInsights.module.scss";

/** A list with at most this many tasks is shown open; a longer one starts with its first project and scope open. */
const OPEN_ALL_UP_TO = 20;

const byName = (a, b) => (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" });

/**
 * Sorts tasks into projects, the scopes of each project and the task groups of each scope. A task that is not in a
 * group sits directly in its scope. Every level keeps the order the tasks came in and counts its tasks.
 */
function groupByStructure(tasks) {
  const projects = new Map();

  tasks.forEach((task) => {
    const projectId = task.project?.id;
    const scopeId = task.scope?.id;

    if (!projects.has(projectId)) projects.set(projectId, { id: projectId, name: task.project?.name, count: 0, scopes: new Map() });
    const project = projects.get(projectId);

    if (!project.scopes.has(scopeId)) project.scopes.set(scopeId, { id: scopeId, name: task.scope?.name, count: 0, tasks: [], groups: new Map() });
    const scope = project.scopes.get(scopeId);

    project.count += 1;
    scope.count += 1;

    if (!task.taskGroup) {
      scope.tasks.push(task);
      return;
    }

    if (!scope.groups.has(task.taskGroup.id)) scope.groups.set(task.taskGroup.id, { id: task.taskGroup.id, name: task.taskGroup.name, count: 0, tasks: [] });
    const group = scope.groups.get(task.taskGroup.id);
    group.count += 1;
    group.tasks.push(task);
  });

  return [...projects.values()].sort(byName).map((project) => ({
    ...project,
    scopes: [...project.scopes.values()]
      .sort(byName)
      .map((scope) => ({ ...scope, groups: [...scope.groups.values()].sort(byName) })),
  }));
}

/** The header of one level of the accordion: what it is called and how many tasks are in it. */
function GroupHeader({ name, count, level }) {
  return (
    <AccordionHeader size="small" className={`${styles.tasksGroupHeader} ${styles[`tasksGroupLevel${level}`]}`}>
      <span className={styles.tasksGroupTitle}>
        <TruncatedText text={name || "Unnamed"} />
        <span className={styles.tasksGroupCount}>{count}</span>
      </span>
    </AccordionHeader>
  );
}

function TaskCards({ tasks, showHours, onNavigate, onEdit }) {
  return (
    <div className={styles.tasksGroupTasks}>
      {tasks.map((task) => (
        <AssignmentRow key={task.id} task={task} showHours={showHours} onNavigate={onNavigate} onEdit={onEdit} />
      ))}
    </div>
  );
}

/**
 * The tasks of a resource, grouped by project, scope and task group, each group a section that opens and closes.
 * Used by Resource Insights and by the list of a row of the planner.
 *
 * @param tasks  what `AssignmentRow` shows, with `project`, `scope` and `taskGroup` each `{ id, name }`
 */
export default function TasksAccordion({ tasks, showHours, onNavigate, onEdit }) {
  const projects = useMemo(() => groupByStructure(tasks), [tasks]);
  const openAll = tasks.length <= OPEN_ALL_UP_TO;
  const cards = { showHours, onNavigate, onEdit };

  return (
    <Accordion multiple collapsible defaultOpenItems={projects.map((project) => project.id)} className={styles.tasksAccordion}>
      {projects.map((project, projectIndex) => (
        <AccordionItem key={project.id} value={project.id}>
          <GroupHeader name={project.name} count={project.count} level={1} />
          <AccordionPanel>
            <Accordion multiple collapsible defaultOpenItems={project.scopes.filter((_, scopeIndex) => openAll || (projectIndex === 0 && scopeIndex === 0)).map((scope) => scope.id)}>
              {project.scopes.map((scope) => (
                <AccordionItem key={scope.id} value={scope.id}>
                  <GroupHeader name={scope.name} count={scope.count} level={2} />
                  <AccordionPanel>
                    {scope.tasks.length > 0 && <TaskCards tasks={scope.tasks} {...cards} />}
                    {scope.groups.length > 0 && (
                      <Accordion multiple collapsible defaultOpenItems={openAll ? scope.groups.map((group) => group.id) : []}>
                        {scope.groups.map((group) => (
                          <AccordionItem key={group.id} value={group.id}>
                            <GroupHeader name={group.name} count={group.count} level={3} />
                            <AccordionPanel>
                              <TaskCards tasks={group.tasks} {...cards} />
                            </AccordionPanel>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
